import { cn } from "@/lib/utils";
import {
  accentClasses,
  type IndustryAccent,
} from "../industryTheme/industryTheme.util";

export interface IndustryChallenge {
  title: string;
  text: string;
}

interface IndustryChallengeCardsProps {
  cards: IndustryChallenge[];
  // Optional dark band under the cards (jewellery's "What works").
  callout?: { label: string; text: string };
  accent?: IndustryAccent;
}

// "Where a <shop> loses customers" - the preview's `.jc-grid`. Big numbered
// cards: the first in the solid accent, the second in ink, the rest in the
// soft accent, each with a faint oversized "01", "02"... behind the copy.
const IndustryChallengeCards = ({
  cards,
  callout,
  accent = "brand",
}: IndustryChallengeCardsProps) => {
  const tone = accentClasses[accent];
  const cardTone = (index: number) =>
    index === 0
      ? cn(tone.solid, "text-white")
      : index === 1
        ? "bg-ink text-white"
        : cn(tone.soft, "text-ink");

  return (
    <div>
      <div
        className={cn(
          "grid gap-5 md:grid-cols-2",
          // Four cards sit 2 x 2 rather than 3 + 1.
          cards.length !== 4 && "lg:grid-cols-3",
        )}
      >
        {cards.map((card, index) => (
          <article
            key={card.title}
            className={cn(
              "relative grid min-h-[220px] content-end gap-2.5 overflow-hidden rounded-[30px] px-7 pt-8 pb-9 sm:min-h-[250px] sm:px-[30px] sm:pt-[34px]",
              cardTone(index),
            )}
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -top-2 right-[18px] text-[110px] leading-none font-extrabold tracking-[-8px] opacity-[0.14] sm:text-[130px]"
            >
              {String(index + 1).padStart(2, "0")}
            </span>
            <h3 className="relative text-[1.3rem] leading-snug font-bold tracking-[-0.01em]">
              {card.title}
            </h3>
            <p
              className={cn(
                "relative text-[15.5px] leading-relaxed",
                index < 2 ? "text-white/90" : "text-ink-muted",
              )}
            >
              {card.text}
            </p>
          </article>
        ))}
      </div>
      {callout && (
        <div className="mt-5 rounded-[30px] bg-ink px-7 py-[22px] text-white">
          <b className="text-[15.5px] font-bold text-primary">{callout.label}</b>
          <p className="mt-1.5 text-[1.1rem] leading-relaxed text-white">
            {callout.text}
          </p>
        </div>
      )}
    </div>
  );
};

export default IndustryChallengeCards;
